import {
  ExclamationTriangleIcon,
  MegaphoneIcon,
  RocketLaunchIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/20/solid";
import { useAuth, useUser } from "@clerk/clerk-react";
import { useUserRole, getUserType } from "../utils/helperMethods";
import {
  isAfterMaintenance,
  isDuringMaintenance,
  isPreMaintenance,
} from "../utils/maintenance";

const BANNERS = {
  sponsor: [
    {
      id: "sponsor-notice",
      phase: "Leading Up",
      icon: ExclamationTriangleIcon,
      title: "Scheduled Maintenance Notice: April 2–6",
      body:
        "From April 2-5, Platform users will not be able to request, review, or manage in-process Vendor Qualification Questionnaires (VQQs) within the Platform. Full VQQ functionality will be restored on April 6. Thank you for your patience as we work to improve the platform.",
      theme: "amber",
    },
    {
      id: "sponsor-active",
      phase: "During Maintenance",
      icon: WrenchScrewdriverIcon,
      title: "Scheduled Maintenance in Progress: April 2–6",
      body:
        "The platform is currently undergoing scheduled maintenance. Platform users will not be able to request, review, or manage in-process Vendor Qualification Questionnaires (VQQs) within the Platform. Full VQQ functionality will be restored on April 6. Thank you for your patience as we work to improve the platform.",
      theme: "blue",
    },
    {
      id: "sponsor-post",
      phase: "After Maintenance",
      icon: RocketLaunchIcon,
      title: "New Diligent VQQ Templates Are Now Live",
      body:
        "We've launched a new set of Diligent Vendor Qualification Questionnaire (VQQ) templates — now the platform default — built on years of audit experience, regulatory expertise, and client feedback. In-progress VQQs have been refreshed automatically, with previously provided vendor responses retained and reapplied where applicable. No action is required from your team. Questions? Contact your Client Success Manager.",
      theme: "purple",
    },
  ],
  vendor: [
    {
      id: "vendor-notice",
      phase: "Leading Up",
      icon: ExclamationTriangleIcon,
      title: "Scheduled Maintenance Notice: April 2–6",
      body:
        "Prior to April 2nd, please approve all answers in any in-progress VQQ responses. Approved answers will be saved to your Knowledge Base and remain accessible after maintenance is complete. Any unapproved answers will be permanently removed. Thank you for your patience as we work to improve the platform.",
      theme: "amber",
    },
    {
      id: "vendor-active",
      phase: "During Maintenance",
      icon: WrenchScrewdriverIcon,
      title: "Scheduled Maintenance in Progress: April 2–6",
      body:
        "The platform is currently undergoing scheduled maintenance. Some DiligentRespond features may be unavailable during this time. We expect full service to be restored by April 6th. Thank you for your patience as we work to improve the platform.",
      theme: "blue",
    },
    {
      id: "vendor-post",
      phase: "After Maintenance",
      icon: MegaphoneIcon,
      title:
        "Platform Update: New Diligent Pharma VQQ Templates Are Now Live",
      body:
        "Diligent Pharma Vendor Qualification Questionnaire (VQQ) templates are now the platform standard, reflecting extensive industry experience and current regulatory expectations. If you had VQQs in progress prior to the maintenance window, you may receive a new request using the updated Diligent Pharma template — your previously provided responses have been retained in your Knowledge Base and can be auto-filled into the new questionnaire so you can pick up right where you left off. Questions? Reach out to your Client Success contact.",
      theme: "purple",
    },
  ],
};

const THEMES = {
  amber: {
    bg: "bg-amber-50",
    border: "border-amber-400",
    iconColor: "text-amber-400",
    title: "text-amber-800",
    body: "text-amber-700",
  },
  blue: {
    bg: "bg-blue-50",
    border: "border-blue-400",
    iconColor: "text-blue-400",
    title: "text-blue-800",
    body: "text-blue-700",
  },
  purple: {
    bg: 'bg-[#F3F0FA]',
    border: 'border-[#CFC6EF]',
    iconColor: 'text-[#7C6BB5]',
    title: 'text-[#4A3880]',
    body: 'text-[#5B4A8A]',
  },
};

/**
 * 0 = Leading Up, 1 = During Maintenance, 2 = After maintenance (templates live).
 * Index 2 is shown from April 6th 00:00 (local time) onward.
 */
function getMaintenanceBannerIndex(now = new Date()) {
  if (isPreMaintenance(now)) return 0;
  if (isDuringMaintenance(now)) return 1;
  if (isAfterMaintenance(now)) return 2;
  return null;
}

/**
 * Vendor (Respond) and sponsor (360) only. Excludes platform and backend admins.
 */
function useMaintenancePortalKey() {
  const userRole = useUserRole();
  const { user } = useUser();
  const subRoles = user?.publicMetadata?.sub_role ?? [];
  const userType = getUserType(userRole, subRoles);

  if (
    userRole === "Backend User" ||
    userRole === "Backend Master User" ||
    userRole === "Diligent Admin"
  ) {
    return null;
  }
  if (userType === "admin") return null;

  const isProviderRole =
    userRole === "Provider User" || userRole === "Provider Master User";

  if (userType === "vendor" || isProviderRole) return "vendor";
  if (userType === "sponsor") return "sponsor";
  if (userRole === "Sponsor Admin") return "sponsor";

  return null;
}

export default function MaintenanceBanner() {
  const { isSignedIn } = useAuth();
  const portalKey = useMaintenancePortalKey();
  const bannerIndex = getMaintenanceBannerIndex();

  if (!isSignedIn || !portalKey || bannerIndex === null) return null;

  const banners = BANNERS[portalKey];
  const banner = banners[bannerIndex];
  const theme = THEMES[banner.theme];
  const Icon = banner.icon;

  return (
    <div
      // className={`${theme.bg} border-b border-l-4 ${theme.border} px-4 py-3`}
      className={`${theme.bg} ${theme.border} px-4 py-3`}
      role="region"
      aria-label="Maintenance announcements"
    >
      <div className="flex items-start gap-3">
        <Icon
          className={`h-5 w-5 flex-shrink-0 mt-0.5 ${theme.iconColor}`}
          aria-hidden="true"
        />

        <div className="flex-1 min-w-0">
          <p className={`text-sm font-semibold ${theme.title}`}>
            {banner.title}
          </p>
          <p className={`mt-1 text-sm ${theme.body}`}>{banner.body}</p>
        </div>
      </div>
    </div>
  );
}
